import React, { useState } from 'react';
import { API_URL } from '../utils/constants';
import {
  Box, Typography, TextField, Button, Stepper, Step, StepLabel,
  Card, CardContent, Grid, RadioGroup, Radio, FormControlLabel,
  Select, MenuItem, FormControl, InputLabel, Checkbox,
  Alert, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';

const steps = ['Personal Info', 'Academic Info', 'Why CWC?', 'Review & Submit'];

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY',
  'LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND',
  'OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'
];

import { getActiveColleges } from '../data/colleges';

const collegeOptions = [
  ...getActiveColleges().map(c => ({ id: c.id, name: c.name })),
  { id: 'other', name: 'Other' },
];

const validationSchemas = [
  Yup.object({
    name: Yup.string().required('Full name is required'),
    email: Yup.string().email('Invalid email').required('Email is required'),
    phone: Yup.string().required('Phone is required'),
    street: Yup.string().required('Street address is required'),
    city: Yup.string().required('City is required'),
    state: Yup.string().required('State is required'),
    zip: Yup.string().matches(/^\d{5}(-\d{4})?$/, 'Invalid ZIP code').required('ZIP is required'),
  }),
  Yup.object({
    college: Yup.string().required('College is required'),
    college_other: Yup.string().when('college', { is: 'other', then: (s) => s.required('College name is required') }),
    year: Yup.string().required('Year is required'),
    gpa: Yup.number().min(3.0, 'GPA must be 3.0 or higher').max(4.0, 'GPA cannot exceed 4.0').required('GPA is required'),
    cert_name: Yup.string().when('year', { is: 'junior', then: (s) => s.required('Certification name required for juniors') }),
    cert_provider: Yup.string().when('year', { is: 'junior', then: (s) => s.required('Certification provider required') }),
    cert_number: Yup.string(),
  }),
  Yup.object({
    why_cybersecurity: Yup.string().min(200, 'Please write at least 200 characters').required('This field is required'),
    career_goals: Yup.string().min(100, 'Please write at least 100 characters').required('This field is required'),
  }),
  Yup.object({
    confirm_accurate: Yup.boolean().oneOf([true], 'You must confirm information is accurate'),
    agree_conduct: Yup.boolean().oneOf([true], 'You must agree to the Code of Conduct'),
    understand_process: Yup.boolean().oneOf([true], 'You must acknowledge the process'),
    sms_consent: Yup.boolean(), // optional opt-in
  }),
];

function InternRegistrationForm({ onSuccess }) {
  const [activeStep, setActiveStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  const formik = useFormik({
    initialValues: {
      name: '', email: '', phone: '', street: '', city: '', state: '', zip: '',
      college: '', college_other: '', year: '', gpa: '',
      cert_name: '', cert_provider: '', cert_number: '',
      why_cybersecurity: '', career_goals: '',
      confirm_accurate: false, agree_conduct: false, understand_process: false,
      sms_consent: false,
    },
    validationSchema: validationSchemas[activeStep],
    onSubmit: async (values) => {
      if (activeStep < steps.length - 1) {
        setActiveStep(prev => prev + 1);
      } else {
        setSubmitting(true);
        setError('');
        try {
          const payload = {
            registration_stage: 'initial_application',
            name: values.name,
            email: values.email,
            phone: values.phone,
            address: { street: values.street, city: values.city, state: values.state, zip: values.zip },
            college: values.college === 'other' ? values.college_other : values.college,
            gpa: values.gpa,
            year: values.year,
            ...(values.year === 'junior' && {
              certification: { name: values.cert_name, provider: values.cert_provider, number: values.cert_number }
            }),
            sms_consent: !!values.sms_consent,
            sms_consent_at: values.sms_consent ? new Date().toISOString() : null,
            sms_consent_text: values.sms_consent
              ? 'I agree to receive operational SMS from Cyber Wise Corp regarding intern job opportunities, booking confirmations, and schedule reminders. Msg & data rates may apply. Reply HELP for help, STOP to cancel.'
              : null,
          };
          const response = await fetch(`${API_URL}/intern/register`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
          const data = await response.json();
          if (response.ok) {
            setSuccess(data);
          } else {
            setError(data.error || 'Registration failed. Please try again.');
          }
        } catch (err) {
          setError('Network error. Please check your connection.');
        }
        setSubmitting(false);
      }
    },
  });

  const handleBack = () => setActiveStep(prev => prev - 1);
  const getCollegeName = () => formik.values.college === 'other' ? formik.values.college_other : collegeOptions.find(c => c.id === formik.values.college)?.name || '';

  // Once submitted, show the success card and hide the form entirely
  if (success) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
          Application Submitted!
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
          Welcome to Cyber Wise Corp, <strong>{formik.values.name}</strong>! Your application has been received.
          We've sent a confirmation to <strong>{formik.values.email}</strong>.
        </Typography>

        {success.intern_id && (
          <Box sx={{ backgroundColor: 'grey.100', p: 2, borderRadius: 2, maxWidth: 400, mx: 'auto', mb: 3 }}>
            <Typography variant="caption" color="text.secondary">Your Application ID</Typography>
            <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
              {success.intern_id}
            </Typography>
          </Box>
        )}

        <Card sx={{ maxWidth: 500, mx: 'auto', mb: 3, textAlign: 'left' }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>What Happens Next</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography variant="body2"><strong>1.</strong> Check your email for login credentials to the intern portal</Typography>
              <Typography variant="body2"><strong>2.</strong> Record and upload a 3-5 minute video answering 3 key questions</Typography>
              <Typography variant="body2"><strong>3.</strong> Complete onboarding training and pass a 40-question certification test (80% minimum)</Typography>
              <Typography variant="body2"><strong>4.</strong> Submit your instructor referral and ID verification</Typography>
              <Typography variant="body2"><strong>5.</strong> Perform your first assessment with phone support from a CWC mentor</Typography>
            </Box>
          </CardContent>
        </Card>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Total time from application to first paid assessment: typically 2-3 weeks.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            href="https://interns.cyberwisecorp.com"
            sx={{ background: 'linear-gradient(135deg, #2563eb, #7c3aed)', px: 4 }}
          >
            Go to Intern Portal
          </Button>
          <Button
            variant="outlined"
            href="https://cyberwisecorp.com"
          >
            Back to Home
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Stepper activeStep={activeStep} sx={{ mb: 4 }} alternativeLabel>
        {steps.map(label => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
      </Stepper>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}

      <form onSubmit={formik.handleSubmit}>
        {/* Step 1: Personal Info */}
        {activeStep === 0 && (
          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, background: 'linear-gradient(135deg, #2563eb, #7c3aed)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Join the CWC Team
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Earn $140 per completed assessment while building your cybersecurity career.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Full Name" name="name" required
                  value={formik.values.name} onChange={formik.handleChange} onBlur={formik.handleBlur}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Email Address" name="email" type="email" required
                  value={formik.values.email} onChange={formik.handleChange} onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Phone Number" name="phone" required
                  value={formik.values.phone} onChange={formik.handleChange} onBlur={formik.handleBlur}
                  error={formik.touched.phone && Boolean(formik.errors.phone)}
                  helperText={formik.touched.phone && formik.errors.phone} />
              </Grid>
              <Grid item xs={12}><Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>Mailing Address</Typography></Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Street Address" name="street" required
                  value={formik.values.street} onChange={formik.handleChange} onBlur={formik.handleBlur}
                  error={formik.touched.street && Boolean(formik.errors.street)}
                  helperText={formik.touched.street && formik.errors.street} />
              </Grid>
              <Grid item xs={12} sm={5}>
                <TextField fullWidth label="City" name="city" required
                  value={formik.values.city} onChange={formik.handleChange} onBlur={formik.handleBlur}
                  error={formik.touched.city && Boolean(formik.errors.city)}
                  helperText={formik.touched.city && formik.errors.city} />
              </Grid>
              <Grid item xs={6} sm={4}>
                <FormControl fullWidth required error={formik.touched.state && Boolean(formik.errors.state)}>
                  <InputLabel>State</InputLabel>
                  <Select name="state" label="State" value={formik.values.state} onChange={formik.handleChange}>
                    {US_STATES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField fullWidth label="ZIP Code" name="zip" required
                  value={formik.values.zip} onChange={formik.handleChange} onBlur={formik.handleBlur}
                  error={formik.touched.zip && Boolean(formik.errors.zip)}
                  helperText={formik.touched.zip && formik.errors.zip} />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Step 2: Academic Info */}
        {activeStep === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>Academic Information</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>College</InputLabel>
                  <Select name="college" label="College" value={formik.values.college} onChange={formik.handleChange}>
                    {collegeOptions.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              {formik.values.college === 'other' && (
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="College Name" name="college_other" required
                    value={formik.values.college_other} onChange={formik.handleChange} onBlur={formik.handleBlur}
                    error={formik.touched.college_other && Boolean(formik.errors.college_other)}
                    helperText={formik.touched.college_other && formik.errors.college_other} />
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Year</Typography>
                <RadioGroup row name="year" value={formik.values.year} onChange={formik.handleChange}>
                  <FormControlLabel value="senior" control={<Radio />} label="Senior" />
                  <FormControlLabel value="junior" control={<Radio />} label="Junior" />
                </RadioGroup>
                {formik.touched.year && formik.errors.year && (
                  <Typography variant="caption" color="error">{formik.errors.year}</Typography>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="GPA" name="gpa" type="number" required
                  inputProps={{ step: 0.1, min: 0, max: 4.0 }}
                  value={formik.values.gpa} onChange={formik.handleChange} onBlur={formik.handleBlur}
                  error={formik.touched.gpa && Boolean(formik.errors.gpa)}
                  helperText={(formik.touched.gpa && formik.errors.gpa) || 'Minimum 3.0 required'} />
              </Grid>

              {formik.values.year === 'junior' && (
                <>
                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ mt: 1 }}>
                      Junior applicants must hold a recognized cybersecurity certification (CompTIA Security+, CEH, etc.)
                    </Alert>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth label="Certification Name" name="cert_name" required
                      placeholder="e.g., CompTIA Security+"
                      value={formik.values.cert_name} onChange={formik.handleChange} onBlur={formik.handleBlur}
                      error={formik.touched.cert_name && Boolean(formik.errors.cert_name)}
                      helperText={formik.touched.cert_name && formik.errors.cert_name} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth label="Certification Provider" name="cert_provider" required
                      placeholder="e.g., CompTIA"
                      value={formik.values.cert_provider} onChange={formik.handleChange} onBlur={formik.handleBlur}
                      error={formik.touched.cert_provider && Boolean(formik.errors.cert_provider)}
                      helperText={formik.touched.cert_provider && formik.errors.cert_provider} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth label="Certificate # or Date" name="cert_number"
                      value={formik.values.cert_number} onChange={formik.handleChange} />
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
        )}

        {/* Step 3: Why CWC? */}
        {activeStep === 2 && (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>Tell Us About Yourself</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Help us understand your passion for cybersecurity and where you're headed.
            </Typography>
            <TextField fullWidth label="Why are you interested in cybersecurity?" name="why_cybersecurity"
              multiline rows={4} required sx={{ mb: 3 }}
              value={formik.values.why_cybersecurity} onChange={formik.handleChange} onBlur={formik.handleBlur}
              error={formik.touched.why_cybersecurity && Boolean(formik.errors.why_cybersecurity)}
              helperText={(formik.touched.why_cybersecurity && formik.errors.why_cybersecurity) ||
                `${formik.values.why_cybersecurity.length}/200 characters minimum`} />
            <TextField fullWidth label="What are your career goals after graduation?" name="career_goals"
              multiline rows={3} required sx={{ mb: 3 }}
              value={formik.values.career_goals} onChange={formik.handleChange} onBlur={formik.handleBlur}
              error={formik.touched.career_goals && Boolean(formik.errors.career_goals)}
              helperText={(formik.touched.career_goals && formik.errors.career_goals) ||
                `${formik.values.career_goals.length}/100 characters minimum`} />
            <Alert severity="info">
              In the next steps after initial approval, you'll upload a 3-minute video introduction and complete an online cybersecurity assessment (80% minimum score).
            </Alert>
          </Box>
        )}

        {/* Step 4: Review & Submit */}
        {activeStep === 3 && (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>Review Your Application</Typography>
            <Card sx={{ mb: 3, backgroundColor: 'grey.50' }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Name</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{formik.values.name}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                    <Typography variant="body1">{formik.values.email}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
                    <Typography variant="body1">{formik.values.phone}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Address</Typography>
                    <Typography variant="body1">{formik.values.street}, {formik.values.city}, {formik.values.state} {formik.values.zip}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2" color="text.secondary">College</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{getCollegeName()}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2" color="text.secondary">Year / GPA</Typography>
                    <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>{formik.values.year} / {formik.values.gpa}</Typography>
                  </Grid>
                  {formik.values.year === 'junior' && (
                    <Grid item xs={12} sm={4}>
                      <Typography variant="subtitle2" color="text.secondary">Certification</Typography>
                      <Typography variant="body1">{formik.values.cert_name}</Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>

            <Box sx={{ mb: 3 }}>
              <FormControlLabel control={<Checkbox name="confirm_accurate" checked={formik.values.confirm_accurate} onChange={formik.handleChange} />}
                label="I confirm that all information provided is accurate and truthful." />
              {formik.touched.confirm_accurate && formik.errors.confirm_accurate && (
                <Typography variant="caption" color="error" display="block" sx={{ ml: 4 }}>{formik.errors.confirm_accurate}</Typography>
              )}

              <FormControlLabel control={<Checkbox name="agree_conduct" checked={formik.values.agree_conduct} onChange={formik.handleChange} />}
                label="I agree to the CWC Intern Code of Conduct and privacy protocols." />
              {formik.touched.agree_conduct && formik.errors.agree_conduct && (
                <Typography variant="caption" color="error" display="block" sx={{ ml: 4 }}>{formik.errors.agree_conduct}</Typography>
              )}

              <FormControlLabel control={<Checkbox name="understand_process" checked={formik.values.understand_process} onChange={formik.handleChange} />}
                label="I understand I will need to complete a video introduction, cybersecurity assessment (80% minimum), and instructor referral before final approval." />
              {formik.touched.understand_process && formik.errors.understand_process && (
                <Typography variant="caption" color="error" display="block" sx={{ ml: 4 }}>{formik.errors.understand_process}</Typography>
              )}
            </Box>

            {/* SMS opt-in — explicit, unchecked by default. Required by carriers
                (TCR / A2P 10DLC) for any campaign that sends transactional SMS. */}
            <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, backgroundColor: '#fafbff' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="sms_consent"
                    checked={formik.values.sms_consent}
                    onChange={formik.handleChange}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                    <strong>SMS notifications (optional).</strong>{' '}
                    I agree to receive operational SMS from Cyber Wise Corp regarding <em>intern job opportunities, booking confirmations, and schedule reminders</em>. Msg &amp; data rates may apply. Reply <strong>HELP</strong> for help, <strong>STOP</strong> to cancel. See our{' '}
                    <a href="https://cyberwisecorp.com/privacy" style={{ color: '#7c3aed' }} target="_blank" rel="noopener noreferrer">Privacy Policy</a> and{' '}
                    <a href="https://cyberwisecorp.com/terms#sms" style={{ color: '#7c3aed' }} target="_blank" rel="noopener noreferrer">SMS Terms</a>.
                  </Typography>
                }
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 4 }}>
                Leaving this unchecked is fine — you&apos;ll still receive job opportunity emails.
              </Typography>
            </Box>
          </Box>
        )}

        {/* Navigation */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button disabled={activeStep === 0} onClick={handleBack} variant="outlined" sx={{ px: 3 }}>Back</Button>
          <Button type="submit" variant="contained" disabled={submitting}
            sx={{ px: 4, background: 'linear-gradient(135deg, #2563eb, #7c3aed)', fontWeight: 600,
              '&:hover': { background: 'linear-gradient(135deg, #1d4ed8, #6d28d9)' } }}>
            {submitting ? <CircularProgress size={24} color="inherit" /> :
              activeStep === steps.length - 1 ? 'Submit Application' : 'Next'}
          </Button>
        </Box>
      </form>

      {/* Success Dialog */}
      <Dialog open={Boolean(success)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ textAlign: 'center', pt: 4 }}>
          <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 1 }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Application Submitted!</Typography>
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center' }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Welcome to Cyber Wise Corp, {formik.values.name}! Your application has been received.
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Next Steps:</Typography>
          <Box sx={{ textAlign: 'left', maxWidth: 400, mx: 'auto', mb: 2 }}>
            <Typography variant="body1" sx={{ mb: 1 }}>1. Check your email for login credentials</Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>2. Upload your 3-minute video introduction</Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>3. Complete the online cybersecurity assessment</Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>4. Submit your instructor referral and ID</Typography>
          </Box>
          {success && success.intern_id && (
            <Box sx={{ backgroundColor: 'grey.50', p: 2, borderRadius: 2, mb: 2 }}>
              <Typography variant="caption" color="text.secondary">Your Application ID</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>{success.intern_id}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button variant="contained" onClick={() => { setSuccess(null); onSuccess?.(); }}
            sx={{ background: 'linear-gradient(135deg, #2563eb, #7c3aed)' }}>
            Got It
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default InternRegistrationForm;
